import React from "react";
import {
  MdArrowBack,
  MdCloudUpload,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdInsertDriveFile,
} from "react-icons/md";
import Card from "components/card";
import { reportTemplateService } from "services/admin/reportTemplateService";
import { reportService } from "services/admin/reportService";
import { useLanguage } from "context/LanguageContext";
import type {
  ReportTemplate,
  TemplateField,
  SessionReport,
  ReportFieldValue,
} from "types/domain";

const DEFAULT_MAX_FILES = 5;

interface Props {
  sessionId: string;
  existingReport?: SessionReport | null;
  onCancel: () => void;
  onSaved: () => void;
}

const ReportSubmitForm: React.FC<Props> = ({
  sessionId,
  existingReport,
  onCancel,
  onSaved,
}) => {
  const [templates, setTemplates] = React.useState<ReportTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string>(
    existingReport?.template_id ?? ""
  );
  const [fieldValues, setFieldValues] = React.useState<
    Record<string, string | number | null>
  >(() => {
    if (!existingReport) return {};
    const vals: Record<string, string | number | null> = {};
    for (const fv of existingReport.field_values) {
      if (fv.text_value != null) vals[fv.template_field_id] = fv.text_value;
      else if (fv.number_value != null)
        vals[fv.template_field_id] = fv.number_value;
    }
    return vals;
  });
  const [fileInputs, setFileInputs] = React.useState<
    Record<string, File | null>
  >({});
  const [multiFileInputs, setMultiFileInputs] = React.useState<
    Record<string, File[]>
  >({});
  const [uploadedFiles, setUploadedFiles] = React.useState<
    Record<string, ReportFieldValue[]>
  >(() => {
    if (!existingReport) return {};
    const grouped: Record<string, ReportFieldValue[]> = {};
    for (const fv of existingReport.field_values) {
      if (fv.field_type === "MULTIPLE_FILES") {
        grouped[fv.template_field_id] = [
          ...(grouped[fv.template_field_id] ?? []),
          fv,
        ];
      }
    }
    return grouped;
  });
  const [content, setContent] = React.useState(existingReport?.content ?? "");
  const [saving, setSaving] = React.useState(false);
  const isEditMode = !!existingReport;
  const [error, setError] = React.useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState<
    Record<string, boolean>
  >({});
  const [loading, setLoading] = React.useState(true);
  const { t } = useLanguage();

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await reportTemplateService.getAll();
        setTemplates(data);
        if (existingReport?.template_id) {
          setSelectedTemplateId(existingReport.template_id);
        } else if (data.length > 0) {
          setSelectedTemplateId(data[0].id);
        }
      } catch (err: any) {
        setError(err.message ?? "reportSubmit.loadTemplatesFailed");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [existingReport?.template_id]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const sortedFields = selectedTemplate?.fields
    ? [...selectedTemplate.fields].sort((a, b) => a.sort_order - b.sort_order)
    : [];

  const handleFieldChange = (
    fieldId: string,
    value: string | number | null
  ) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleFileChange = (fieldId: string, file: File | null) => {
    setFileInputs((prev) => ({ ...prev, [fieldId]: file }));
  };

  const getMaxFiles = (field: TemplateField): number => {
    const configured = field.config?.max_files;
    return typeof configured === "number" && configured > 0
      ? configured
      : DEFAULT_MAX_FILES;
  };

  const handleMultiFileAdd = (field: TemplateField, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const maxFiles = getMaxFiles(field);
    const currentCount =
      (uploadedFiles[field.id]?.length ?? 0) +
      (multiFileInputs[field.id]?.length ?? 0);
    const remaining = maxFiles - currentCount;
    if (remaining <= 0) {
      setError(t("reportField.maxFilesReached"));
      return;
    }
    const incoming = Array.from(files);
    const toAdd = incoming.slice(0, remaining);
    if (incoming.length > toAdd.length) {
      setError(t("reportField.maxFilesReached"));
    }
    setMultiFileInputs((prev) => ({
      ...prev,
      [field.id]: [...(prev[field.id] ?? []), ...toAdd],
    }));
  };

  const handleRemoveStagedFile = (fieldId: string, index: number) => {
    setMultiFileInputs((prev) => ({
      ...prev,
      [fieldId]: (prev[fieldId] ?? []).filter((_, i) => i !== index),
    }));
  };

  const handleRemoveUploadedFile = async (fieldId: string, valueId: string) => {
    if (!existingReport) return;
    if (!window.confirm(t("reportField.confirmDeleteFile"))) return;
    try {
      await reportService.deleteFile(existingReport.id, fieldId, valueId);
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] ?? []).filter((fv) => fv.id !== valueId),
      }));
    } catch (err: any) {
      setError(err.message ?? "reportSubmit.deleteFileFailed");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplateId) {
      setError("reportSubmit.templateRequired");
      return;
    }

    // Validate required fields
    for (const field of sortedFields) {
      if (field.is_required && field.field_type !== "FILE") {
        const val = fieldValues[field.id];
        if (val === undefined || val === null || val === "") {
          setError(t("reportSubmit.fieldRequired", { field: field.label }));
          return;
        }
      }
      if (
        field.is_required &&
        field.field_type === "FILE" &&
        !fileInputs[field.id]
      ) {
        setError(t("reportSubmit.fieldRequired", { field: field.label }));
        return;
      }
      if (field.is_required && field.field_type === "MULTIPLE_FILES") {
        const totalFiles =
          (uploadedFiles[field.id]?.length ?? 0) +
          (multiFileInputs[field.id]?.length ?? 0);
        if (totalFiles === 0) {
          setError(t("reportSubmit.fieldRequired", { field: field.label }));
          return;
        }
      }
    }

    setSaving(true);
    setError(null);

    try {
      // Build field_values for non-FILE fields
      const fieldValuePayload = sortedFields
        .filter(
          (f) => f.field_type !== "FILE" && f.field_type !== "MULTIPLE_FILES"
        )
        .map((f) => ({
          template_field_id: f.id,
          text_value:
            f.field_type === "TEXT"
              ? (fieldValues[f.id] as string) || undefined
              : undefined,
          number_value:
            f.field_type === "NUMBER" &&
            fieldValues[f.id] !== undefined &&
            fieldValues[f.id] !== null
              ? Number(fieldValues[f.id])
              : undefined,
        }));

      const report = isEditMode
        ? await reportService.update(existingReport!.id, {
            content: content || undefined,
            field_values: fieldValuePayload,
          })
        : await reportService.create(sessionId, {
            template_id: selectedTemplateId,
            content: content || undefined,
            field_values: fieldValuePayload,
          });

      // Upload files
      const fileFields = sortedFields.filter((f) => f.field_type === "FILE");
      for (const field of fileFields) {
        const file = fileInputs[field.id];
        if (file) {
          setUploadProgress((prev) => ({ ...prev, [field.id]: true }));
          await reportService.uploadFile(report.id, field.id, file);
          setUploadProgress((prev) => ({ ...prev, [field.id]: false }));
        }
      }

      // Upload staged files for MULTIPLE_FILES fields
      const multiFileFields = sortedFields.filter(
        (f) => f.field_type === "MULTIPLE_FILES"
      );
      for (const field of multiFileFields) {
        const files = multiFileInputs[field.id] ?? [];
        if (files.length > 0) {
          setUploadProgress((prev) => ({ ...prev, [field.id]: true }));
          for (const file of files) {
            await reportService.uploadFile(report.id, field.id, file);
          }
          setUploadProgress((prev) => ({ ...prev, [field.id]: false }));
        }
      }

      onSaved();
    } catch (err: any) {
      // Service errors may be translation keys (e.g. upload failures).
      setError(err.message ?? "reportSubmit.submitFailed");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: TemplateField) => {
    switch (field.field_type) {
      case "TEXT":
        return (
          <textarea
            value={(fieldValues[field.id] as string) ?? ""}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
            placeholder={t("reportSubmit.fieldPlaceholder", {
              field: field.label.toLowerCase(),
            })}
          />
        );
      case "NUMBER":
        return (
          <input
            type="number"
            step="any"
            value={fieldValues[field.id] ?? ""}
            onChange={(e) =>
              handleFieldChange(
                field.id,
                e.target.value ? parseFloat(e.target.value) : null
              )
            }
            className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
            placeholder={t("reportSubmit.fieldPlaceholder", {
              field: field.label.toLowerCase(),
            })}
          />
        );
      case "FILE":
        return (
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line px-4 py-2 text-sm text-ink-muted hover:bg-surface-hover">
              <MdCloudUpload className="h-5 w-5" />
              {fileInputs[field.id]
                ? fileInputs[field.id]!.name
                : t("reportSubmit.chooseFile")}
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf,.xlsx,.xls,.xlsm,.xlsb,.doc,.docx,.ppt,.pptx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                onChange={(e) =>
                  handleFileChange(field.id, e.target.files?.[0] ?? null)
                }
              />
            </label>
            {uploadProgress[field.id] && (
              <span className="text-xs text-blue-500">
                {t("reportSubmit.uploading")}
              </span>
            )}
          </div>
        );
      case "MULTIPLE_FILES": {
        const maxFiles = getMaxFiles(field);
        const staged = multiFileInputs[field.id] ?? [];
        const uploaded = uploadedFiles[field.id] ?? [];
        const totalCount = staged.length + uploaded.length;
        const limitReached = totalCount >= maxFiles;
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <label
                className={`flex items-center gap-2 rounded-lg border border-dashed border-line px-4 py-2 text-sm text-ink-muted ${ limitReached ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:bg-surface-hover"
                }`}
              >
                <MdCloudUpload className="h-5 w-5" />
                {t("reportField.addFile")}
                <input
                  type="file"
                  multiple
                  disabled={limitReached}
                  className="hidden"
                  accept="image/*,.pdf,.xlsx,.xls,.xlsm,.xlsb,.doc,.docx,.ppt,.pptx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={(e) => {
                    handleMultiFileAdd(field, e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
              <span className="text-xs text-ink-subtle">
                {totalCount}/{maxFiles}
              </span>
              {uploadProgress[field.id] && (
                <span className="text-xs text-blue-500">
                  {t("reportSubmit.uploading")}
                </span>
              )}
            </div>

            {uploaded.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-ink-subtle">
                  {t("reportField.uploadedFiles")}
                </p>
                {uploaded.map((fv) => (
                  <div
                    key={fv.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-1.5 text-sm"
                  >
                    <span className="flex items-center gap-2 truncate text-gray-600 dark:text-gray-300">
                      <MdInsertDriveFile className="h-4 w-4 shrink-0" />
                      <span className="truncate">{fv.file_name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUploadedFile(field.id, fv.id)}
                      className="shrink-0 rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      title={t("reportField.removeFile")}
                    >
                      <MdDelete className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {staged.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-ink-subtle">
                  {t("reportField.pendingFiles")}
                </p>
                {staged.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-1.5 text-sm"
                  >
                    <span className="flex items-center gap-2 truncate text-gray-600 dark:text-gray-300">
                      <MdInsertDriveFile className="h-4 w-4 shrink-0" />
                      <span className="truncate">{file.name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveStagedFile(field.id, index)}
                      className="shrink-0 rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      title={t("reportField.removeFile")}
                    >
                      <MdClose className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card extra="p-6 mt-3">
        <p className="text-ink-muted">{t("state.loading")}</p>
      </Card>
    );
  }

  return (
    <div className="mt-3">
      <Card extra="p-6">
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={onCancel}
            className="rounded p-1.5 text-ink-muted hover:bg-surface-hover"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold text-ink">
            {isEditMode
              ? t("reportSubmit.editTitle")
              : t("reportSubmit.createTitle")}
          </h2>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {t(error)}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Template selector */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("reportSubmit.template")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTemplateId}
              disabled={isEditMode}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                setFieldValues({});
                setFileInputs({});
                setMultiFileInputs({});
                setUploadedFiles({});
              }}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm disabled:opacity-60 dark:bg-surface-muted dark:text-white"
            >
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}{" "}
                  {tpl.is_default ? t("reportSubmit.defaultSuffix") : ""}
                </option>
              ))}
            </select>
          </div>

          {/* General content */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("reportSubmit.generalNote")}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-line dark:bg-surface-muted dark:text-white"
              placeholder={t("reportSubmit.generalNotePlaceholder")}
            />
          </div>

          {/* Dynamic fields */}
          {sortedFields.map((field) => (
            <div key={field.id}>
              <label className="mb-1 block text-sm font-medium text-ink-muted">
                {field.label}
                {field.is_required && (
                  <span className="ml-1 text-red-500">*</span>
                )}
              </label>
              {renderField(field)}
            </div>
          ))}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {saving ? (
                t("action.saving")
              ) : (
                <>
                  <MdCheckCircle className="h-4 w-4" />
                  {isEditMode
                    ? t("reportSubmit.update")
                    : t("reportSubmit.submit")}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-line px-6 py-2 text-sm font-medium text-ink-muted hover:bg-surface-hover"
            >
              {t("action.cancel")}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ReportSubmitForm;
